import { Controller } from "@hotwired/stimulus"

// Appointment form helper: narrows the practitioner list to those who offer
// the chosen service, and shows the free start times for that practitioner
// and day. Picking a time fills the regular start-time select, which stays
// available for any other time.
export default class extends Controller {
  static targets = ["service", "employee", "coEmployee", "date", "startTime", "slots", "status"]
  static values = {
    url: String,
    except: Number,
    loadingText: { type: String, default: "…" },
    emptyText: { type: String, default: "" },
    errorText: { type: String, default: "" }
  }

  connect() {
    this.filterEmployees()
    this.refresh()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.abort?.abort()
  }

  serviceChanged() {
    this.filterEmployees()
    this.refresh()
  }

  filterEmployees() {
    if (!this.hasServiceTarget || !this.hasEmployeeTarget) return
    const option = this.serviceTarget.selectedOptions[0]
    const ids = (option?.dataset.employeeIds || "").split(",").filter(Boolean)
    const options = Array.from(this.employeeTarget.options)
    if (ids.length === 0) {
      options.forEach(o => { o.hidden = false; o.disabled = false })
      return
    }
    let selectedAllowed = false
    options.forEach(o => {
      const allowed = o.value === "" || ids.includes(o.value)
      o.hidden = !allowed
      o.disabled = !allowed
      if (allowed && o.selected) selectedAllowed = true
    })
    if (!selectedAllowed) {
      const first = options.find(o => !o.disabled && o.value !== "")
      if (first) this.employeeTarget.value = first.value
    }
  }

  refresh() {
    if (!this.hasSlotsTarget) return
    const serviceId = this.hasServiceTarget ? this.serviceTarget.value : ""
    const employeeId = this.hasEmployeeTarget ? this.employeeTarget.value : ""
    const date = this.hasDateTarget ? this.dateTarget.value : ""
    clearTimeout(this.timer)
    if (!serviceId || !employeeId || !date) { this.render([]); return }
    this.timer = setTimeout(() => this.load(serviceId, employeeId, date, this.coEmployeeId()), 150)
  }

  // The second practitioner counts only while the co-therapy block is open.
  coEmployeeId() {
    if (!this.hasCoEmployeeTarget) return ""
    const panel = this.coEmployeeTarget.closest("[data-toggle-section-target='panel']")
    if (panel && panel.classList.contains("hidden")) return ""
    return this.coEmployeeTarget.value
  }

  async load(serviceId, employeeId, date, coEmployeeId = "") {
    this.abort?.abort()
    this.abort = new AbortController()
    this.statusTarget.textContent = this.loadingTextValue
    const params = new URLSearchParams({ service_id: serviceId, employee_id: employeeId, date })
    if (coEmployeeId) params.set("co_employee_id", coEmployeeId)
    if (this.exceptValue) params.set("except", this.exceptValue)
    try {
      const response = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" }, signal: this.abort.signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.render(data.slots || [])
    } catch (error) {
      if (error.name === "AbortError") return
      this.slotsTarget.innerHTML = ""
      this.statusTarget.textContent = this.errorTextValue
    }
  }

  render(slots) {
    this.slots = slots
    const current = this.hasStartTimeTarget ? this.startTimeTarget.value : null
    this.slotsTarget.innerHTML = ""
    slots.forEach(time => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.time = time
      button.className = `btn btn-xs tabular ${time === current ? "btn-primary" : "btn-outline"}`
      button.textContent = time
      button.addEventListener("click", () => this.pick(time))
      this.slotsTarget.appendChild(button)
    })
    this.statusTarget.textContent = slots.length ? "" : this.emptyTextValue
  }

  highlight() {
    if (this.slots) this.render(this.slots)
  }

  pick(time) {
    const select = this.startTimeTarget
    if (!Array.from(select.options).some(o => o.value === time)) {
      const option = document.createElement("option")
      option.value = time
      option.textContent = time
      select.appendChild(option)
    }
    select.value = time
    select.dispatchEvent(new Event("change", { bubbles: true }))
    this.highlight()
  }
}
